import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useQuery } from '@tanstack/react-query';
import { CustomerOutstandingRowSchema, SupplierOutstandingRowSchema } from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { getData } from '../../shared/api/client';
import { ExportButton } from '../../shared/export/ExportButton';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { sum } from '../finance/money';
import { Money } from './common';

/**
 * Customer Outstanding / Receivables report (CLR-13; FRD-FIN-062, FIN-131): per client, what was
 * invoiced, credited and received, what is outstanding and overdue, and the advances held. Each
 * client drills to its invoices, filtered, in the invoice list (FRD-FIN-137).
 */
export function CustomerOutstandingPage() {
  const rows = useQuery({
    queryKey: ['report', 'customer-outstanding'],
    queryFn: () =>
      getData(
        '/api/v1/finance/receivables/customer-outstanding',
        z.array(CustomerOutstandingRowSchema),
      ),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Customer outstanding"
        description="Receivables by client from issued invoices, credit notes and posted receipts."
        actions={
          <ExportButton
            recordType="finance.customer_invoice"
            href="/api/v1/finance/receivables/customer-outstanding/export"
          />
        }
      />
      <Section>
        {rows.isPending && <LoadingState label="Loading receivables" />}
        {rows.isError && <ErrorState message={rows.error.message} />}
        {rows.data?.length === 0 && <EmptyState title="No receivables" />}
        {rows.data !== undefined && rows.data.length > 0 && (
          <Table size="small" aria-label="Customer outstanding">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell align="right">Invoiced</TableCell>
                <TableCell align="right">Credited</TableCell>
                <TableCell align="right">Received</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="right">Overdue</TableCell>
                <TableCell align="right">Advances held</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.data.map((row) => (
                <TableRow key={row.clientId} hover>
                  <TableCell>
                    <RecordLink to={`/customer-invoices?client=${row.clientId}&status=issued`}>
                      {row.clientLabel ?? row.clientId}
                    </RecordLink>
                  </TableCell>
                  <Money value={row.invoiced} />
                  <Money value={row.credited} />
                  <Money value={row.received} />
                  <Money value={row.outstanding} bold />
                  <Money value={row.overdue} />
                  <Money value={row.advances} />
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <Money value={sum(rows.data.map((r) => r.invoiced))} bold />
                <Money value={sum(rows.data.map((r) => r.credited))} bold />
                <Money value={sum(rows.data.map((r) => r.received))} bold />
                <Money value={sum(rows.data.map((r) => r.outstanding))} bold />
                <Money value={sum(rows.data.map((r) => r.overdue))} bold />
                <Money value={sum(rows.data.map((r) => r.advances))} bold />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

/**
 * Supplier Outstanding / Payables report (CLR-13; FRD-FIN-077, FIN-132): per supplier, what was
 * invoiced, credited and paid, what is outstanding and overdue, and the advances paid ahead.
 */
export function SupplierOutstandingPage() {
  const rows = useQuery({
    queryKey: ['report', 'supplier-outstanding'],
    queryFn: () =>
      getData(
        '/api/v1/finance/payables/supplier-outstanding',
        z.array(SupplierOutstandingRowSchema),
      ),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Supplier outstanding"
        description="Payables by supplier from posted invoices, credit notes and payments."
        actions={
          <ExportButton
            recordType="finance.supplier_invoice"
            href="/api/v1/finance/payables/supplier-outstanding/export"
          />
        }
      />
      <Section>
        {rows.isPending && <LoadingState label="Loading payables" />}
        {rows.isError && <ErrorState message={rows.error.message} />}
        {rows.data?.length === 0 && <EmptyState title="No payables" />}
        {rows.data !== undefined && rows.data.length > 0 && (
          <Table size="small" aria-label="Supplier outstanding">
            <TableHead>
              <TableRow>
                <TableCell>Supplier</TableCell>
                <TableCell align="right">Invoiced</TableCell>
                <TableCell align="right">Credited</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="right">Overdue</TableCell>
                <TableCell align="right">Advances paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.data.map((row) => (
                <TableRow key={row.supplierId} hover>
                  <TableCell>
                    <RecordLink to={`/supplier-invoices?supplier=${row.supplierId}&status=posted`}>
                      {row.supplierLabel ?? row.supplierId}
                    </RecordLink>
                  </TableCell>
                  <Money value={row.invoiced} />
                  <Money value={row.credited} />
                  <Money value={row.paid} />
                  <Money value={row.outstanding} bold />
                  <Money value={row.overdue} />
                  <Money value={row.advances} />
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <Money value={sum(rows.data.map((r) => r.invoiced))} bold />
                <Money value={sum(rows.data.map((r) => r.credited))} bold />
                <Money value={sum(rows.data.map((r) => r.paid))} bold />
                <Money value={sum(rows.data.map((r) => r.outstanding))} bold />
                <Money value={sum(rows.data.map((r) => r.overdue))} bold />
                <Money value={sum(rows.data.map((r) => r.advances))} bold />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

const ReconciliationStatusSchema = z.object({
  asAt: z.string(),
  rows: z.array(
    z.object({
      accountId: z.string(),
      code: z.string(),
      name: z.string(),
      balance: z.string(),
      capability: z.string(),
      status: z.string().nullable(),
      registers: z.array(
        z.object({
          id: z.string(),
          code: z.string(),
          name: z.string(),
          accountStatus: z.string(),
          unreconciled: z.number(),
          reconciled: z.number(),
          latestOpenMovement: z.string().nullable(),
        }),
      ),
    }),
  ),
});

/**
 * Reconciliation status (FRD-FIN-032, FIN-097): each account the COA marks for reconciliation,
 * its balance, and — for the bank and cash registers — how many movements are still unreconciled.
 * Other accounts are reconciled by the capability that performs it, which the report names.
 */
export function ReconciliationStatusPage() {
  const status = useQuery({
    queryKey: ['report', 'reconciliation-status'],
    queryFn: () => getData('/api/v1/finance/reconciliation-status', ReconciliationStatusSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Reconciliation status"
        description="Accounts marked Reconciliation Required, with their balance and status."
      />
      {status.isPending && <LoadingState label="Loading the reconciliation status" />}
      {status.isError && <ErrorState message={status.error.message} />}
      {status.data !== undefined && (
        <Section title={`As at ${status.data.asAt}`}>
          {status.data.rows.length === 0 ? (
            <EmptyState title="No account is marked Reconciliation Required" />
          ) : (
            <Table size="small" aria-label="Reconciliation status">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Registers</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status.data.rows.map((row) => (
                  <TableRow key={row.accountId}>
                    <TableCell>
                      <RecordLink to={`/accounts/${row.accountId}`}>
                        {row.code} {row.name}
                      </RecordLink>
                    </TableCell>
                    <Money value={row.balance} />
                    <TableCell>
                      {row.capability === 'bank_cash'
                        ? row.status === 'reconciled'
                          ? 'Reconciled'
                          : 'Open items'
                        : 'Reconciled with its own process (e.g. VAT control reconciliation)'}
                    </TableCell>
                    <TableCell>
                      {row.registers.length === 0
                        ? '—'
                        : row.registers.map((register) => (
                            <div key={register.id}>
                              <RecordLink to="/bank-cash">{register.code}</RecordLink>:{' '}
                              {register.unreconciled} unreconciled, {register.reconciled} reconciled
                              {register.latestOpenMovement === null
                                ? ''
                                : ` (latest open ${register.latestOpenMovement})`}
                            </div>
                          ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </Stack>
  );
}

const OpeningReconciliationSchema = z.array(
  z.object({
    accountId: z.string(),
    accountCode: z.string(),
    accountName: z.string(),
    counterparty: z.string(),
    glTotal: z.string(),
    subLedgerTotal: z.string(),
    difference: z.string(),
    parties: z.array(z.object({ id: z.string(), amount: z.string() })),
  }),
);

/**
 * The opening balances' sub-ledger reconciliation (FRD-FIN-142): each control account of the
 * opening journal that carries a Client or Supplier, its GL total against the total by
 * counterparty, and any difference.
 */
export function OpeningBalanceReconciliationPage() {
  const [params] = useSearchParams();
  const journalId = params.get('journalId') ?? '';
  const rows = useQuery({
    queryKey: ['report', 'opening-balance-reconciliation', journalId],
    enabled: journalId !== '',
    queryFn: () =>
      getData(
        `/api/v1/ledger/reports/opening-balance-reconciliation?journalId=${journalId}`,
        OpeningReconciliationSchema,
      ),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Opening balance reconciliation"
        description="Sub-ledger opening balances by client or supplier against their control totals."
      />
      {journalId === '' && <EmptyState title="Open this from an opening-balance journal" />}
      {rows.isPending && journalId !== '' && <LoadingState label="Loading the reconciliation" />}
      {rows.isError && <ErrorState message={rows.error.message} />}
      {rows.data !== undefined && (
        <Section>
          {rows.data.length === 0 ? (
            <EmptyState title="The journal has no counterparty control accounts" />
          ) : (
            <Table size="small" aria-label="Opening balance reconciliation">
              <TableHead>
                <TableRow>
                  <TableCell>Control account</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell align="right">GL total</TableCell>
                  <TableCell align="right">Sub-ledger total</TableCell>
                  <TableCell align="right">Difference</TableCell>
                  <TableCell align="right">Counterparties</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.data.map((row) => (
                  <TableRow key={row.accountId}>
                    <TableCell>
                      {row.accountCode} {row.accountName}
                    </TableCell>
                    <TableCell>{row.counterparty}</TableCell>
                    <Money value={row.glTotal} />
                    <Money value={row.subLedgerTotal} />
                    <Money value={row.difference} bold={row.difference !== '0.00'} />
                    <TableCell align="right">{row.parties.length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </Stack>
  );
}
